import { isUuid } from '@/lib/validation';
import type { ServiceClient } from '../http/client';
import { ApiError } from '../http/errors';

export interface CurrentAuthorization {
  assigned: boolean;
  revision: number | null;
  role_bits: number;
  domain_bits: Record<string, number>;
}
/**
 * 개발 서버 전용 스위치(VITE_DEV_SKIP_ACCESS_GUARD=true) — 역할·도메인 비트를 클라이언트에서 확인하지 않는다.
 * 코어가 권한을 아직 배정하지 않은 계정으로 화면만 확인할 때 쓴다. 서버는 그대로 403을 줄 수 있고,
 * production 빌드(import.meta.env.DEV=false)에서는 값과 무관하게 꺼진다.
 */
export const skipAccessGuard =
  import.meta.env.DEV && import.meta.env.VITE_DEV_SKIP_ACCESS_GUARD === 'true';
const mask = (value: unknown): value is number => Number.isSafeInteger(value) && Number(value) >= 0;

export async function readCurrentAuthorization(
  client: ServiceClient,
  signal?: AbortSignal,
): Promise<CurrentAuthorization> {
  const value = await client.get<unknown>('users/me/authorization', { signal });
  if (!value || typeof value !== 'object') throw invalid();
  const data = value as Partial<CurrentAuthorization>;
  if (
    typeof data.assigned !== 'boolean' ||
    !(data.revision === null || mask(data.revision)) ||
    !mask(data.role_bits) ||
    !data.domain_bits ||
    typeof data.domain_bits !== 'object' ||
    Array.isArray(data.domain_bits) ||
    !Object.values(data.domain_bits).every(mask)
  )
    throw invalid();
  return data as CurrentAuthorization;
}
function invalid() {
  return new ApiError({
    kind: 'invalid-response',
    service: 'core',
    message: '업무 권한을 확인하지 못했습니다. 관리자에게 문의해 주세요.',
  });
}
/** Current role and assignment establish operator readiness; domain grants remain independent. */
export function canReadManagementWorkspace(value: CurrentAuthorization): boolean {
  if (skipAccessGuard) return true;
  return value.assigned && (value.role_bits & (4 | 8)) !== 0;
}

/** domain_bits already includes server overrides; never add role defaults in the client. */
export function canReadDomain(
  value: CurrentAuthorization,
  domain: string,
  requiredBits: number,
): boolean {
  return (
    canReadManagementWorkspace(value) &&
    ((value.domain_bits[domain] ?? 0) & requiredBits) === requiredBits
  );
}

export function canReadProperties(value: CurrentAuthorization): boolean {
  return canReadDomain(value, 'property', 1);
}

/** The server has already applied overrides; bit 2 grants Property writes. */
export function canWriteProperties(value: CurrentAuthorization): boolean {
  return canReadDomain(value, 'property', 2);
}

export function canManageTypeSales(value: CurrentAuthorization): boolean {
  return canReadDomain(value, 'booking', 32);
}

export function canReadBookings(value: CurrentAuthorization): boolean {
  return canReadDomain(value, 'booking', 2);
}

/** User directory reads require their own effective USER bit 2. */
export function canReadUsers(value: CurrentAuthorization): boolean {
  return canReadDomain(value, 'user', 2);
}

/** The server has already applied overrides; bit 2 grants property setting changes. */
export function canChangeApplicationSetting(value: CurrentAuthorization): boolean {
  return canReadDomain(value, 'property', 2);
}

/** 비밀이 없는 API namespace + 세대 + 업무 + 지점. U3의 모든 운영 query도 이 prefix를 사용한다. */
export const coreQueryKey = (
  apiNamespace: string,
  generation: number,
  domain: string,
  propertyId?: string,
) =>
  [
    'core',
    apiNamespace,
    generation,
    domain,
    ...(propertyId === undefined ? [] : [propertyId]),
  ] as const;

/**
 * 이 토큰의 주인(코어 users/me의 user_id). 권한 응답에는 신원이 없어, 같은 권한의 다른 계정과 구분하는 근거는 이 값뿐이다.
 * 형식이 틀리면 신원을 증명하지 못한 것으로 보고 던진다.
 */
export async function readCurrentUserId(
  client: ServiceClient,
  signal?: AbortSignal,
): Promise<string> {
  const value = await client.get<unknown>('users/me', { signal });
  if (
    !value ||
    typeof value !== 'object' ||
    !('user_id' in value) ||
    typeof value.user_id !== 'string' ||
    !isUuid(value.user_id)
  )
    throw invalid();
  return value.user_id;
}
